import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { redundancyApi, type PlayoutTargetInput, type VideoRouterInput } from '../../lib/api.js'
import { useAuthUser, usePlayoutState } from '../../store/index.js'
import type { PlayoutTarget, PlayoutTargetHealth, RedundancyConfigResponse } from '../../types/index.js'

const defaultTarget: PlayoutTargetInput = {
  name: 'Playout B', host: '', port: 5250, casparChannel: 1, fps: 50,
  routerInput: null, priority: 1, enabled: true,
}
const defaultRouter: VideoRouterInput = { host: '', port: 9990, outputIndex: 0, enabled: false }

export const PlayoutRedundancySettings = memo(function PlayoutRedundancySettings({ channelId }: { channelId: string | null }) {
  const user = useAuthUser()
  const playout = usePlayoutState()
  const [data, setData] = useState<RedundancyConfigResponse | null>(null)
  const [drafts, setDrafts] = useState<Record<string, PlayoutTargetInput>>({})
  const [newTarget, setNewTarget] = useState<PlayoutTargetInput | null>(null)
  const [router, setRouter] = useState<VideoRouterInput>(defaultRouter)
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState('')

  const canConfigure = user?.role === 'ADMIN'
  const canControl = user?.role === 'ADMIN' || Boolean(user?.permissions.includes('PLAYOUT_CONTROL'))

  const load = useCallback(async () => {
    if (!channelId) { setData(null); return }
    const response = await redundancyApi.get(channelId)
    setData(response)
    setDrafts(Object.fromEntries(response.targets.map(target => [target.id, toInput(target)])))
    setRouter(response.router ? {
      host: response.router.host,
      port: response.router.port,
      outputIndex: response.router.outputIndex,
      enabled: response.router.enabled,
    } : defaultRouter)
  }, [channelId])

  useEffect(() => { void load().catch(error => setMessage(error.message)) }, [load])

  const run = useCallback(async (key: string, action: () => Promise<unknown>, success: string) => {
    setBusy(key); setMessage('')
    try {
      await action()
      setMessage(success)
      await load()
      return true
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error))
      return false
    } finally { setBusy(null) }
  }, [load])

  const createTarget = useCallback(async () => {
    if (!channelId || !newTarget) return
    const created = await run('create', () => redundancyApi.createTarget(channelId, newTarget), 'Playout ajouté')
    if (created) setNewTarget(null)
  }, [channelId, newTarget, run])

  const health = useMemo(() => new Map((playout?.targets ?? data?.runtime?.targets ?? []).map(target => [target.id, target])), [data?.runtime?.targets, playout?.targets])
  const activeTargetId = playout?.activeTargetId ?? data?.runtime?.activeTargetId ?? data?.state?.activeTargetId ?? null
  const armed = playout?.autoFailoverArmed ?? data?.runtime?.autoFailoverArmed ?? data?.state?.autoFailoverArmed ?? false

  if (!channelId) return null

  return (
    <section className="settings-section" aria-labelledby="redundancy-title">
      <div className="settings-section__heading">
        <h2 id="redundancy-title" className="settings-section__title">Playouts & redondance</h2>
        <p className="settings-section__description">Deux CasparCG en miroir chaud, bascule VideoHub confirmée et retour exclusivement manuel.</p>
      </div>

      <div className="settings-redundancy__policy">
        <label className="settings-option-row settings-option-row--selectable">
          <input
            type="checkbox"
            checked={armed}
            disabled={!canControl || busy !== null}
            onChange={event => void run('armed', () => redundancyApi.setArmed(channelId, event.target.checked), event.target.checked ? 'Bascule automatique armée' : 'Bascule automatique désarmée')}
          />
          <span className="settings-option-row__copy">
            <span className="settings-option-row__title">Bascule automatique</span>
            <span className="settings-option-row__description">
              Panne confirmée pendant {formatDuration(data?.policy.confirmMs)} · cooldown {formatDuration(data?.policy.cooldownMs)} · {data?.policy.maxSwitchesPerHour ?? 3} bascules max./h · aucun retour automatique{data?.policy.failOnFrozenFrames ? ' · détection image figée active' : ''}.
            </span>
          </span>
        </label>
      </div>

      <div className="settings-redundancy__targets">
        {data?.targets.map(target => (
          <TargetCard
            key={target.id}
            target={target}
            draft={drafts[target.id] ?? toInput(target)}
            health={health.get(target.id)}
            active={activeTargetId === target.id}
            canConfigure={canConfigure}
            canControl={canControl}
            busy={busy}
            onChange={patch => setDrafts(current => ({ ...current, [target.id]: { ...current[target.id], ...patch } }))}
            onSave={() => run(`save:${target.id}`, () => redundancyApi.updateTarget(channelId, target.id, drafts[target.id]), `${target.name} enregistré`)}
            onTest={() => run(`test:${target.id}`, () => redundancyApi.testTarget(channelId, target.id), `Test ${target.name} envoyé`)}
            onSwitch={() => run(`switch:${target.id}`, () => redundancyApi.switchTarget(channelId, target.id), `Bascule vers ${target.name} envoyée`)}
            onDelete={() => {
              if (window.confirm(`Supprimer ${target.name} ?`)) void run(`delete:${target.id}`, () => redundancyApi.deleteTarget(channelId, target.id), `${target.name} supprimé`)
            }}
          />
        ))}
        {newTarget && (
          <TargetEditor
            title="Nouveau playout"
            value={newTarget}
            disabled={busy !== null}
            onChange={patch => setNewTarget(current => current ? { ...current, ...patch } : current)}
            actions={<>
              <button className="tp-button tp-button--info" disabled={!newTarget.host || busy !== null} onClick={() => void createTarget()}>Ajouter</button>
              <button className="tp-button" onClick={() => setNewTarget(null)}>Annuler</button>
            </>}
          />
        )}
      </div>
      {canConfigure && (data?.targets.length ?? 0) < 2 && !newTarget && (
        <button className="tp-button" onClick={() => setNewTarget({ ...defaultTarget, casparChannel: data?.targets[0]?.casparChannel ?? 1 })}>+ Ajouter le secours</button>
      )}

      <div className="settings-redundancy__router">
        <div className="settings-section__heading">
          <h3 className="settings-section__title">VideoHub</h3>
          <p className="settings-section__description">Les index d’entrée et de sortie sont ceux du protocole VideoHub, à partir de 0.</p>
        </div>
        <div className="settings-redundancy__form-grid">
          <Field label="Hôte" value={router.host} disabled={!canConfigure} onChange={value => setRouter(current => ({ ...current, host: value }))} />
          <NumberField label="Port" value={router.port} disabled={!canConfigure} onChange={value => setRouter(current => ({ ...current, port: value }))} />
          <NumberField label="Sortie programme" value={router.outputIndex} disabled={!canConfigure} onChange={value => setRouter(current => ({ ...current, outputIndex: value }))} />
          <label className="settings-redundancy__checkbox"><input type="checkbox" checked={router.enabled} disabled={!canConfigure} onChange={event => setRouter(current => ({ ...current, enabled: event.target.checked }))} /> Actif</label>
        </div>
        <div className="settings-actions">
          {canConfigure && <button className="tp-button tp-button--info" disabled={!router.host || busy !== null} onClick={() => void run('router-save', () => redundancyApi.saveRouter(channelId, router), 'VideoHub enregistré')}>Sauver</button>}
          {canControl && <button className="tp-button" disabled={!data?.router || busy !== null} onClick={() => void run('router-test', () => redundancyApi.testRouter(channelId), 'Test VideoHub envoyé')}>Tester le routeur</button>}
        </div>
      </div>

      {(data?.history.length ?? 0) > 0 && (
        <div className="settings-redundancy__history">
          <h3 className="settings-section__title">Dernières bascules</h3>
          <ol>
            {data!.history.slice(0, 5).map(event => <li key={event.id}><time>{new Date(event.createdAt).toLocaleString('fr-FR')}</time><span>{event.trigger} · {event.reason}</span><span>{event.routerAcked ? 'VideoHub confirmé' : 'Échec routeur'}</span></li>)}
          </ol>
        </div>
      )}
      {message && <p className="settings-redundancy__message" role="status">{message}</p>}
    </section>
  )
})

const TargetCard = memo(function TargetCard(props: {
  target: PlayoutTarget
  draft: PlayoutTargetInput
  health?: PlayoutTargetHealth
  active: boolean
  canConfigure: boolean
  canControl: boolean
  busy: string | null
  onChange: (patch: Partial<PlayoutTargetInput>) => void
  onSave: () => void
  onTest: () => void
  onSwitch: () => void
  onDelete: () => void
}) {
  const { target, draft, health, active, canConfigure, canControl, busy } = props
  return (
    <div className={`settings-redundancy__target${active ? ' is-active' : ''}`}>
      <div className="settings-redundancy__target-head">
        <div><strong>{target.name}</strong><span>{active ? 'À L’ANTENNE' : health?.role ?? 'MIRROR'}</span></div>
        <span className={`settings-redundancy__health is-${health?.healthy ? 'ok' : 'danger'}`}>{health?.healthy ? 'SAIN' : health?.connected ? 'DÉGRADÉ' : 'HORS LIGNE'}</span>
      </div>
      <TargetEditor title="" value={draft} disabled={!canConfigure || busy !== null} onChange={props.onChange} />
      <div className="settings-redundancy__telemetry">frame {health?.frame ?? '—'} · AMCP {health?.probe?.latencyMs ?? '—'} ms · médias {health?.preflight ? health.preflight.ok ? 'conformes' : `${health.preflight.missing.length} absents` : 'non vérifiés'}</div>
      <div className="settings-actions">
        {canConfigure && <button className="tp-button tp-button--info" disabled={busy !== null} onClick={props.onSave}>Sauver</button>}
        {canControl && <button className="tp-button" disabled={busy !== null} onClick={props.onTest}>Tester</button>}
        {canControl && !active && <button className="tp-button tp-button--danger" disabled={!health?.healthy || busy !== null} onClick={props.onSwitch}>Basculer</button>}
        {canConfigure && !active && <button className="tp-button" disabled={busy !== null} onClick={props.onDelete}>Supprimer</button>}
      </div>
    </div>
  )
})

const TargetEditor = memo(function TargetEditor({ title, value, disabled, onChange, actions }: {
  title: string
  value: PlayoutTargetInput
  disabled: boolean
  onChange: (patch: Partial<PlayoutTargetInput>) => void
  actions?: ReactNode
}) {
  return <div className="settings-redundancy__editor">
    {title && <strong>{title}</strong>}
    <div className="settings-redundancy__form-grid">
      <Field label="Nom" value={value.name} disabled={disabled} onChange={name => onChange({ name })} />
      <Field label="Hôte" value={value.host} disabled={disabled} onChange={host => onChange({ host })} />
      <NumberField label="Port AMCP" value={value.port} disabled={disabled} onChange={port => onChange({ port })} />
      <NumberField label="Canal Caspar" value={value.casparChannel} disabled={disabled} onChange={casparChannel => onChange({ casparChannel })} />
      <NumberField label="FPS" value={value.fps} disabled={disabled} onChange={fps => onChange({ fps })} />
      <NumberField label="Entrée VideoHub" value={value.routerInput ?? 0} disabled={disabled} onChange={routerInput => onChange({ routerInput })} />
      <NumberField label="Priorité" value={value.priority} disabled={disabled} onChange={priority => onChange({ priority })} />
      <label className="settings-redundancy__checkbox"><input type="checkbox" checked={value.enabled} disabled={disabled} onChange={event => onChange({ enabled: event.target.checked })} /> Actif</label>
    </div>
    {actions && <div className="settings-actions">{actions}</div>}
  </div>
})

const Field = memo(function Field({ label, value, disabled, onChange }: { label: string; value: string; disabled: boolean; onChange: (value: string) => void }) {
  return <label><span>{label}</span><input className="tp-input" value={value} disabled={disabled} onChange={event => onChange(event.target.value)} /></label>
})

const NumberField = memo(function NumberField({ label, value, disabled, onChange }: { label: string; value: number; disabled: boolean; onChange: (value: number) => void }) {
  return <label><span>{label}</span><input className="tp-input" type="number" min={0} value={value} disabled={disabled} onChange={event => onChange(Number(event.target.value))} /></label>
})

function toInput(target: PlayoutTarget): PlayoutTargetInput {
  return { name: target.name, host: target.host, port: target.port, casparChannel: target.casparChannel, fps: target.fps, routerInput: target.routerInput, priority: target.priority, enabled: target.enabled }
}

function formatDuration(milliseconds: number | undefined): string {
  if (milliseconds === undefined) return '—'
  return milliseconds >= 60_000 && milliseconds % 60_000 === 0
    ? `${milliseconds / 60_000} min`
    : `${milliseconds / 1_000} s`
}
